'use client';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { BlogCard } from './blog-card';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

export function BlogsList({ locale }: { locale: string }) {
  const { t } = useTranslations();
  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 7,
          order: 'desc',
          orderBy: 'created_at',
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data).slice(1) ?? [];
  const isLoading = blogsQuery.isLoading;

  return (
    <section className={cn('custom-section', 'ea-section__xxl-y')}>
      <div
        className={cn(
          'custom-container',
          'grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 sm:gap-y-8 lg:gap-y-12',
        )}
      >
        {isLoading &&
          Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="col-span-1 h-[345px] w-full animate-pulse rounded-[8px] bg-neutral-100 sm:h-[360px] lg:h-[210px]"
            />
          ))}
        {!isLoading && blogs.length === 0 && (
          <Paragraph
            color="muted-foreground"
            size="lg"
            className="col-span-1 w-full text-center sm:col-span-2"
          >
            Nenašli sme žiadne blogy.
          </Paragraph>
        )}
        {!isLoading &&
          blogs.length > 0 &&
          blogs.map(blog => <BlogCard blog={blog} key={blog.id} semanticTag="h2" />)}
        <div
          className={cn('col-span-1 flex w-full items-center justify-center sm:col-span-2', {
            hidden: !blogsQuery.hasNextPage,
          })}
        >
          <Button
            onClick={() => blogsQuery.fetchNextPage()}
            isLoading={blogsQuery.isFetchingNextPage}
            className="w-full rounded-full sm:w-fit"
          >
            {t('text338')}
          </Button>
        </div>
      </div>
    </section>
  );
}
